// Master list of selectable scheduling-tier perks (display only).
// Admins pick which perks appear on each tier package; the public checkout
// renders the matching labels as bullets. None of these drive automated
// scheduling logic — they are operator-fulfilled promises. The one behavior
// actually enforced (waiving per-request constraint fees) is a separate
// per-tier boolean (`*_tier_waives_fees`).

export interface SchedulingPerk {
  key: string;
  label: string;
}

export const SCHEDULING_PERKS: SchedulingPerk[] = [
  { key: "standard_entry", label: "Standard tournament entry" },
  { key: "best_effort_schedule", label: "Best-effort schedule assignment" },
  { key: "submit_hard_constraints", label: "Submit hard constraints (coach conflicts, blackout windows)" },
  { key: "honored_first", label: "Honored before standard teams in the solver" },
  { key: "draft_peek", label: "24hr peek at draft schedule before publish" },
  { key: "no_internal_conflicts", label: "No internal coach conflicts across your club's teams" },
  { key: "venue_clustering", label: "Venue clustering (your teams play same site when possible)" },
  { key: "aligned_times", label: "Aligned start/end times for bus logistics" },
  { key: "featured_court", label: "Guaranteed featured court / streamed game slot" },
  { key: "coach_windows", label: "Scheduled during college coach attendance windows" },
  { key: "free_requests", label: "Scheduling requests included at no extra charge" },
];

const PERK_BY_KEY: Record<string, SchedulingPerk> = Object.fromEntries(
  SCHEDULING_PERKS.map((p) => [p.key, p])
);

// Resolve an array of perk keys to display labels, dropping unknown keys.
export function perkLabels(keys: unknown): string[] {
  if (!Array.isArray(keys)) return [];
  return keys
    .map((k) => (typeof k === "string" ? PERK_BY_KEY[k]?.label : undefined))
    .filter((l): l is string => !!l);
}

// Sensible defaults so existing events that have no perks selected still show
// meaningful bullets (matches the previous hardcoded copy).
export const DEFAULT_TIER_PERKS: Record<string, string[]> = {
  priority: ["submit_hard_constraints", "honored_first", "draft_peek"],
  club_priority: [
    "no_internal_conflicts",
    "venue_clustering",
    "aligned_times",
  ],
  showcase: ["featured_court", "coach_windows"],
};
